import useSWRMutation from 'swr/mutation';
import { NudosButton, NudosSearchBar } from 'components/NudosComponents';
import SelectReservationSerialItem from './Components/SelectReservationSerialItem/SelectReservationSerialItem';
import { useState } from 'react';
import { displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { ICreateReservationStockItem, IDeleteReservationStockItem } from 'types/stockOrders';
import { createReservationStock, cancelReservationStock } from 'services/stockOrders';
import { SWR_KEYS } from 'constants/constants';
import useStockReservationState from 'state/useStockReservationState';
import './FloatSelectSerial.scss';

export interface IFloatSelectSerialItem {
  providerName: string;
  serial: string;
  billingNumber: string;
  date: string;
  stockProductId: number;
  isFirtsOut: boolean;
  saleId?: string | number | null;
}

export interface IFloatSelectSerial {
  items: IFloatSelectSerialItem[];
  onSuccess: () => void;
}

const FloatSelectSerial = ({ items, onSuccess }: IFloatSelectSerial) => {
  const { selectedItems, setRefreshStockProductAvailabilityData, currentStockReservation, setCurrentStockReservation } =
    useStockReservationState();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);

  const { trigger: createReservationStockTrigger, isMutating: isLoadingCreateReservationStock } = useSWRMutation(
    SWR_KEYS.createReservationStock,
    async (
      __,
      {
        arg
      }: {
        arg: ICreateReservationStockItem[];
      }
    ) => {
      return await createReservationStock(arg);
    },
    {
      onSuccess: () => {
        displaySuccessNotification({
          customJSXMessage: <>Se ha reservado con éxito una unidad de stock propio</>,
          duration: 3000
        });
        setRefreshStockProductAvailabilityData(true);
        onSuccess();
      }
    }
  );

  const { trigger: cancelReservationStockTrigger, isMutating: isLoadingCancelReservationStock } = useSWRMutation(
    SWR_KEYS.cancelReservationStock,
    async (
      __,
      {
        arg
      }: {
        arg: IDeleteReservationStockItem;
      }
    ) => {
      return await cancelReservationStock(arg);
    },
    {
      onSuccess: () => {
        displaySuccessNotification({
          customJSXMessage: <>Se ha cancelado con éxito la reserva de stock</>,
          duration: 3000
        });
        setRefreshStockProductAvailabilityData(true);
        setCurrentStockReservation(null);
        onSuccess();
      }
    }
  );

  const filteredItems = items.filter(item => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      item.serial.toLowerCase().includes(searchTermLower) ||
      item.billingNumber.toLowerCase().includes(searchTermLower) ||
      item.providerName.toLowerCase().includes(searchTermLower)
    );
  });

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleCreateReservationStock = () => {
    createReservationStockTrigger(selectedItems);
  };

  const handleCancelReservationStock = () => {
    if (!currentStockReservation) return;
    cancelReservationStockTrigger([currentStockReservation.stockProduct.stockProductId]);
  };

  return (
    <div className="floatSelectSerial">
      <div className="mb-4">
        <NudosSearchBar
          handleChange={handleSearch}
          placeholder="Buscar por serial, n° de factura o proveedor"
          hasDeleteButton
          includeMagnifierIcon
        />
      </div>
      <div className="itemsContainer">
        {filteredItems?.map((item, index) => (
          <SelectReservationSerialItem
            key={`${item?.providerName}-${index}`}
            providerName={item?.providerName}
            serial={item?.serial}
            billingNumber={item?.billingNumber}
            date={item?.date}
            isFirtsOut={index <= 2}
            stockProductId={item?.stockProductId}
            isSelected={
              currentStockReservation?.stockProduct?.serial === item.serial || selectedItemId === item.stockProductId
            }
            onSelect={() => {
              setSelectedItemId(item.stockProductId);
              setCurrentStockReservation(null);
            }}
          />
        ))}
      </div>
      <div className="buttonsContainer">
        {currentStockReservation && !selectedItemId && (
          <NudosButton
            isButtonLoading={isLoadingCancelReservationStock}
            isButtonDisabled={isLoadingCancelReservationStock}
            buttonText={isLoadingCancelReservationStock ? 'Quitando reserva...' : 'Quitar reserva'}
            customHeight="18px"
            customWidth="100px"
            handleClick={handleCancelReservationStock}
            colorPalette="secondary"
          />
        )}
        <NudosButton
          isButtonLoading={isLoadingCreateReservationStock}
          isButtonDisabled={isLoadingCreateReservationStock || selectedItems?.length === 0}
          buttonText={isLoadingCreateReservationStock ? 'Aplicando reserva...' : 'Aplicar reserva'}
          customHeight="18px"
          customWidth="100px"
          handleClick={handleCreateReservationStock}
          colorPalette="secondary"
        />
      </div>
    </div>
  );
};

export default FloatSelectSerial;
